/**
 * ʕ•̫͡·•ʔ੭
 * A reusable Pill component with theme and state variants
 * @param label - The main label of the pill
 * @param showSecondaryLabel - Whether to show the secondary label
 * @param secondaryLabel - The secondary label to display
 * @param size - The size of the pill
 * @param icon - The icon to display
 * @param closeIcon - The close icon to display
 * @param showIcon - Whether to show the icon
 * @param showCloseIcon - Whether to show the close icon
 * @param theme - The theme of the pill
 * @param status - The status of the pill
 * @param state - The state of the pill
 * @param type - The type of the pill
 * @param showDropdownIcon - Whether to show the dropdown icon
 * @param dropdownIcon - The dropdown icon to display
 * @param onClick - Callback fired when the pill is clicked
 * @param onCloseClick - Callback fired when the close icon is clicked
 * @param onDropdownClick - Callback fired when the dropdown icon is clicked
 * @param disableRipple - Whether to disable the ripple effect
 */
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import React, { useEffect, useMemo, ElementType } from 'react';
import {
  DEFAULT_CLOSE_ICON,
  DEFAULT_DROPDOWN_ICON,
  DEFAULT_ICON,
  iconSizeConfig,
  paddingConfig,
  pillColorMap,
} from './constants';
import { PillSize, PillState, Props } from './types';

const Label = ({
  label = '',
  size,
  isMain,
  showIcon,
}: {
  label?: string;
  size: PillSize;
  isMain?: boolean;
  showIcon?: boolean;
}) => {
  return (
    <Typography
      fontWeight={500}
      lineHeight="normal"
      variant={size === 'large' ? 'body2' : 'caption'}
      sx={{
        userSelect: 'none',
        letterSpacing: 0,
        ...(isMain && showIcon && { marginLeft: size === 'large' ? '0.6rem' : '0.4rem' }),
      }}
    >
      {label}
    </Typography>
  );
};

export const Pill = ({
  label,
  secondaryLabel,
  size = 'small',
  icon = DEFAULT_ICON,
  showIcon = false,
  showCloseIcon = false,
  closeIcon = DEFAULT_CLOSE_ICON,
  showDropdownIcon = false,
  dropdownIcon = DEFAULT_DROPDOWN_ICON,
  theme = 'light',
  state,
  status = 'info',
  type = 'clickable',
  showSecondaryLabel = false,
  onClick = () => {},
  onCloseClick = () => {},
  onDropdownClick = () => {},
  disableRipple = false,
  disabled = false,
  className = '',
}: Props) => {
  const initialState = useMemo(() => (type === 'readonly' ? 'read' : 'default'), [type]);
  const [currentState, setCurrentState] = React.useState<PillState>(state || initialState);

  useEffect(() => {
    if (state) {
      setCurrentState(state);
    }
  }, [state]);

  const { color, backgroundColor, borderColor } = useMemo(() => {
    return pillColorMap[theme][currentState][status];
  }, [theme, currentState, status]);

  const handleMouseEvents = useMemo(() => {
    if (type !== 'clickable') {
      setCurrentState('read');
      return {};
    }
    setCurrentState('default');
    return {
      onMouseEnter: () => setCurrentState('hover'),
      onMouseLeave: () => setCurrentState('default'),
      onMouseDown: () => setCurrentState('pressed'),
      onMouseUp: () => setCurrentState('hover'),
      style: { cursor: 'pointer' },
    };
  }, [type, setCurrentState]);

  const Component: ElementType = type === 'clickable' ? ButtonBase : Stack;

  return (
    <Component
      disableRipple={disableRipple}
      disabled={disabled}
      className={className}
      sx={{
        padding: paddingConfig[size],
        height: 'fit-content',
        borderRadius: '3.1rem',
        backgroundColor,
        color,
        border: `0.1rem solid ${borderColor}`,
        alignItems: 'center',
        flexDirection: 'row',
        display: 'flex',
        transition: 'all 0.2s ease-in-out',
        '& .MuiSvgIcon-root': {
          fontSize: iconSizeConfig[size],
        },
        '& .MuiCircularProgress-root': {
          color,
        },
      }}
      {...(type === 'clickable' ? { ...handleMouseEvents, onClick } : {})}
    >
      {showIcon && icon}
      <Label label={label} size={size} showIcon={showIcon} isMain />
      {showSecondaryLabel && (
        <>
          <Box
            sx={{
              marginX: '0.8rem',
              width: '0.1rem',
              height: size === 'large' ? '1.2rem' : '1rem',
              backgroundColor: color,
            }}
          />
          <Label label={secondaryLabel} size={size} />
        </>
      )}
      {showCloseIcon &&
        React.cloneElement(closeIcon as React.ReactElement, {
          onClick: (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
            e.stopPropagation();
            onCloseClick?.(e);
          },
          style: { cursor: 'pointer', marginLeft: '0.8rem' },
        })}
      {showDropdownIcon &&
        React.cloneElement(dropdownIcon as React.ReactElement, {
          onClick: (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
            e.stopPropagation();
            onDropdownClick?.(e);
          },
          style: { cursor: 'pointer', marginLeft: '0.8rem' },
        })}
    </Component>
  );
};
